import { appPath } from "../appPath";
import { formatChinaDateTime } from "../time";
import { useEffect, useState } from "react";

import { InstitutionProducts } from "./InstitutionProducts";
import { InstitutionAgentDialog } from "./InstitutionAgentDialog";
import { InstitutionMoreMenu } from "./InstitutionMoreMenu";

import {
  createInstitution,
  disableInstitution,
  enableInstitution,
  getInstitutionCredentials,
  listAssignableInstitutionAgents,
  listInstitutionRecharges,
  listInstitutions,
  rechargeInstitution,
  resetInstitutionPassword,
  updateInstitution,
  type Institution,
  type AgentSummary,
  type InstitutionAgentBinding,
  type InstitutionCredentials,
  type InstitutionRecharge,
  type InstitutionStatus,
  type InstitutionType,
} from "./api";

const PAGE_SIZE = 10;

type InstitutionForm = {
  mode: "create" | "edit";
  institution?: Institution;
  organizationType: InstitutionType;
  name: string;
  creditCode: string;
  administratorName: string;
  administratorMobile: string;
  agentPlatformUserId: string;
  authorizationMaterialRequired: boolean;
};

type Confirmation = {
  action: "edit" | "reset" | "disable" | "enable";
  institution: Institution;
};

type RechargeDialog = {
  institution: Institution;
  amount: string;
  note: string;
  stage: "input" | "confirm";
};

export function institutionTypeLabel(type: InstitutionType): string {
  return {
    LAW_FIRM: "律所",
    FINANCIAL_INSTITUTION: "金融机构",
    OTHER: "其他",
  }[type];
}

export function formatInstitutionCredential(
  account: string,
  password: string,
  loginUrl: string,
): string {
  return `登录地址：${loginUrl}\n登录账号：${account}\n登录密码：${password}`;
}

export function isRechargeAmount(value: string): boolean {
  return /^(?:0\.[0-9]{0,1}[1-9]|0\.[1-9][0-9]?|[1-9]\d*(?:\.\d{1,2})?)$/.test(value);
}

function toCents(value: string): bigint {
  const [integer, decimals = ""] = value.split(".");
  return BigInt(integer || "0") * 100n + BigInt(decimals.padEnd(2, "0").slice(0, 2));
}

function fromCents(value: bigint): string {
  const integer = value / 100n;
  const decimals = String(value % 100n).padStart(2, "0");
  return `${integer}.${decimals}`;
}

export function calculateBalanceAfter(balance: string, amount: string): string {
  return fromCents(toCents(balance) + toCents(amount));
}

function formatCurrency(value: string): string {
  return `¥${Number(value).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

function emptyCreateForm(): InstitutionForm {
  return {
    mode: "create",
    organizationType: "LAW_FIRM",
    name: "",
    creditCode: "",
    administratorName: "",
    administratorMobile: "",
    agentPlatformUserId: "",
    authorizationMaterialRequired: true,
  };
}

export function InstitutionAccounts() {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [organizationType, setOrganizationType] = useState<InstitutionType | "">("");
  const [status, setStatus] = useState<InstitutionStatus | "">("");
  const [agentBinding, setAgentBinding] = useState<InstitutionAgentBinding | "">("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState<InstitutionForm | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [credentials, setCredentials] = useState<InstitutionCredentials | null>(null);
  const [credentialsTitle, setCredentialsTitle] = useState("");
  const [recharge, setRecharge] = useState<RechargeDialog | null>(null);
  const [detail, setDetail] = useState<Institution | null>(null);
  const [productInstitution, setProductInstitution] = useState<Institution | null>(null);
  const [agentInstitution, setAgentInstitution] = useState<Institution | null>(null);
  const [formAgents, setFormAgents] = useState<AgentSummary[]>([]);
  const [formAgentsLoading, setFormAgentsLoading] = useState(false);
  const [history, setHistory] = useState<InstitutionRecharge[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listInstitutions({
        query,
        organizationType,
        status,
        agentBinding,
        page,
        pageSize: PAGE_SIZE,
      });
      setInstitutions(result.items);
      setTotal(result.total);
      if (page > pageCount(result.total)) setPage(pageCount(result.total));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "机构账户列表加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [page, query, organizationType, status, agentBinding]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!detail) return;
    const institution = detail;
    setHistoryLoading(true);
    setModalError("");
    void listInstitutionRecharges(institution.id, historyPage, PAGE_SIZE)
      .then((result) => {
        setHistory(result.items);
        setHistoryTotal(result.total);
      })
      .catch((reason) => setModalError(
        reason instanceof Error ? reason.message : "充值记录加载失败",
      ))
      .finally(() => setHistoryLoading(false));
  }, [detail, historyPage]);

  function search(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  function openEdit(institution: Institution) {
    setModalError("");
    setForm({
      mode: "edit",
      institution,
      organizationType: institution.organization_type,
      name: institution.name,
      creditCode: institution.credit_code,
      administratorName: institution.administrator_name,
      administratorMobile: institution.administrator_mobile,
      agentPlatformUserId: institution.agent?.id ?? "",
      authorizationMaterialRequired: institution.authorization_material_required,
    });
  }

  async function saveEdit(value: InstitutionForm) {
    if (!value.institution) return;
    await updateInstitution(value.institution.id, {
      name: value.name,
      credit_code: value.creditCode,
      administrator_name: value.administratorName,
      administrator_mobile: value.administratorMobile,
      authorization_material_required: value.authorizationMaterialRequired,
    });
    setForm(null);
    setConfirmation(null);
    setToast("机构信息已保存");
    await reload();
  }

  async function submitForm(event: React.FormEvent) {
    event.preventDefault();
    if (!form || submitting) return;
    setModalError("");
    if (
      form.mode === "edit"
      && form.institution
      && (
        form.creditCode !== form.institution.credit_code
        || form.administratorMobile !== form.institution.administrator_mobile
        || form.authorizationMaterialRequired !== form.institution.authorization_material_required
      )
    ) {
      setConfirmation({ action: "edit", institution: form.institution });
      return;
    }
    setSubmitting(true);
    try {
      if (form.mode === "create") {
        const created = await createInstitution({
          organization_type: form.organizationType,
          name: form.name,
          credit_code: form.creditCode,
          administrator_name: form.administratorName,
          administrator_mobile: form.administratorMobile,
          agent_platform_user_id: form.agentPlatformUserId || null,
          authorization_material_required: form.authorizationMaterialRequired,
        });
        setForm(null);
        setCredentials({ account: created.account, password: created.initial_password });
        setCredentialsTitle("机构账户创建成功");
        await reload();
      } else {
        await saveEdit(form);
      }
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  async function writeCredential(value: InstitutionCredentials) {
    if (!navigator.clipboard?.writeText) throw new Error("当前浏览器不支持复制，请更换浏览器后重试");
    await navigator.clipboard.writeText(
      formatInstitutionCredential(value.account, value.password, `${window.location.origin}${appPath("/")}`),
    );
  }

  async function copyCredential(institution: Institution) {
    setError("");
    try {
      const value = await getInstitutionCredentials(institution.id);
      await writeCredential(value);
      setToast("账号密码已复制");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "账号密码复制失败");
    }
  }

  async function copyModalCredential() {
    if (!credentials) return;
    try {
      await writeCredential(credentials);
      setCredentials(null);
      setToast("账号密码已复制");
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "账号密码复制失败");
    }
  }

  async function confirmAction() {
    if (!confirmation || submitting) return;
    setSubmitting(true);
    setModalError("");
    try {
      if (confirmation.action === "edit" && form) {
        await saveEdit(form);
      } else if (confirmation.action === "reset") {
        const value = await resetInstitutionPassword(confirmation.institution.id);
        setConfirmation(null);
        setCredentials(value);
        setCredentialsTitle("密码重置成功");
        await reload();
      } else {
        const action = confirmation.action;
        if (action === "disable") await disableInstitution(confirmation.institution.id);
        else await enableInstitution(confirmation.institution.id);
        setConfirmation(null);
        setToast(action === "disable" ? "机构账户已停用" : "机构账户已启用");
        await reload();
      }
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  function previewRecharge(event: React.FormEvent) {
    event.preventDefault();
    if (!recharge) return;
    if (!isRechargeAmount(recharge.amount)) {
      setModalError("请输入大于 0 且最多两位小数的充值金额");
      return;
    }
    setModalError("");
    setRecharge({ ...recharge, stage: "confirm" });
  }

  async function confirmRecharge() {
    if (!recharge || recharge.stage !== "confirm" || submitting) return;
    setSubmitting(true);
    setModalError("");
    try {
      await rechargeInstitution(recharge.institution.id, {
        amount: recharge.amount,
        note: recharge.note.trim() || undefined,
      });
      setRecharge(null);
      setToast("充值成功");
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "充值失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  function openCreateForm() {
    setModalError("");
    setForm(emptyCreateForm());
    setFormAgentsLoading(true);
    void listAssignableInstitutionAgents()
      .then(setFormAgents)
      .catch((reason) => setModalError(
        reason instanceof Error ? reason.message : "代理列表加载失败",
      ))
      .finally(() => setFormAgentsLoading(false));
  }

  function savedAgent(updated: Institution) {
    setInstitutions((items) => items.map((item) => item.id === updated.id ? updated : item));
    setAgentInstitution(null);
    setToast(updated.agent ? "代理已关联" : "代理关联已解除");
    void reload();
  }

  const pages = pageCount(total);
  const formReady = Boolean(
    form
    && form.name.trim().length >= 2
    && form.creditCode.length === 18
    && form.administratorName.trim().length >= 2
    && /^1\d{10}$/.test(form.administratorMobile),
  );

  return (
    <section className="platform-subaccounts platform-institutions" aria-label="机构账户管理">
      <div className="platform-panel">
        <form className="platform-list-toolbar platform-institution-toolbar" onSubmit={search}>
          <div className="platform-list-search">
            <input
              aria-label="搜索机构账户"
              placeholder="搜索机构名称、编码、信用代码、管理员或手机号"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
            <select aria-label="机构类型" value={organizationType} onChange={(event) => { setOrganizationType(event.target.value as InstitutionType | ""); setPage(1); }}>
              <option value="">全部类型</option>
              <option value="LAW_FIRM">律所</option>
              <option value="FINANCIAL_INSTITUTION">金融机构</option>
              <option value="OTHER">其他</option>
            </select>
            <select aria-label="机构状态" value={status} onChange={(event) => { setStatus(event.target.value as InstitutionStatus | ""); setPage(1); }}>
              <option value="">全部状态</option>
              <option value="ACTIVE">启用</option>
              <option value="DISABLED">停用</option>
            </select>
            <button type="submit">查询</button>
            <select aria-label="代理关联状态" value={agentBinding} onChange={(event) => { setAgentBinding(event.target.value as InstitutionAgentBinding | ""); setPage(1); }}>
              <option value="">全部代理关联</option>
              <option value="BOUND">已绑定代理</option>
              <option value="UNBOUND">未绑定代理</option>
            </select>
            {(query || draftQuery || organizationType || status || agentBinding) && (
              <button className="is-secondary" type="button" onClick={() => { setDraftQuery(""); setQuery(""); setOrganizationType(""); setStatus(""); setAgentBinding(""); setPage(1); }}>重置</button>
            )}
          </div>
          <button className="platform-primary-button" type="button" onClick={openCreateForm}>
            <span aria-hidden="true">＋</span> 创建机构账户
          </button>
        </form>

        <div className="platform-table-wrap">
          <table className="platform-account-table platform-institution-table">
            <thead><tr>
              <th>机构信息</th><th>机构类型</th><th>管理员</th><th>关联代理</th>
              <th>当前余额</th><th>状态</th><th>创建时间</th>
              <th aria-label="操作：产品配置、更多">操作</th>
            </tr></thead>
            <tbody>
              {!loading && !error && institutions.map((institution) => (
                <tr key={institution.id}>
                  <td><strong>{institution.name}</strong><small>{institution.code}{institution.credit_code ? ` · ${institution.credit_code}` : ""}</small></td>
                  <td>{institutionTypeLabel(institution.organization_type)}</td>
                  <td><strong>{institution.administrator_name}</strong><small>{institution.administrator_mobile}</small></td>
                  <td>{institution.agent ? <button className="platform-inline-action platform-agent-link" type="button" onClick={() => setAgentInstitution(institution)}>{institution.agent.name}<small>已关联</small></button> : <button className="platform-inline-action" type="button" onClick={() => setAgentInstitution(institution)}>绑定代理</button>}</td>
                  <td><strong className="platform-balance">{formatCurrency(institution.balance)}</strong></td>
                  <td><span className={`platform-status ${institution.status === "ACTIVE" ? "is-active" : "is-disabled"}`}>{institution.status === "ACTIVE" ? "启用" : "停用"}</span></td>
                  <td>{formatChinaDateTime(institution.created_at)}</td>
                  <td><div className="platform-row-actions">
                    <button type="button" onClick={() => setProductInstitution(institution)}>产品配置</button>
                    <InstitutionMoreMenu>
                      <button type="button" onClick={() => void copyCredential(institution)}>复制账号密码</button>
                      <button type="button" onClick={() => { setModalError(""); setRecharge({ institution, amount: "", note: "", stage: "input" }); }}>充值</button>
                      <button type="button" onClick={() => { setModalError(""); setHistoryPage(1); setDetail(institution); }}>查看详情</button>
                      <button type="button" onClick={() => openEdit(institution)}>编辑机构</button>
                      <button type="button" onClick={() => { setModalError(""); setConfirmation({ action: "reset", institution }); }}>重置密码</button>
                      <button className={institution.status === "ACTIVE" ? "is-danger" : "is-positive"} type="button" onClick={() => { setModalError(""); setConfirmation({ action: institution.status === "ACTIVE" ? "disable" : "enable", institution }); }}>{institution.status === "ACTIVE" ? "停用机构" : "启用机构"}</button>
                    </InstitutionMoreMenu>
                  </div></td>
                </tr>
              ))}
              {loading && <tr><td className="platform-table-state" colSpan={8}>正在加载机构账户…</td></tr>}
              {!loading && error && <tr><td className="platform-table-state is-error" colSpan={8}>{error}<button type="button" onClick={() => void reload()}>重新加载</button></td></tr>}
              {!loading && !error && institutions.length === 0 && <tr><td className="platform-table-state" colSpan={8}>暂无机构账户</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button>
          <span>{page} / {pages}</span>
          <button type="button" disabled={page >= pages || loading} onClick={() => setPage((value) => value + 1)}>下一页</button>
        </div></div>
      </div>

      {form && (
        <div className="platform-modal-layer" role="presentation">
          <form className="platform-modal platform-institution-form" role="dialog" aria-modal="true" aria-labelledby="institution-form-title" onSubmit={submitForm}>
            <div className="platform-modal-heading"><h2 id="institution-form-title">{form.mode === "create" ? "创建机构账户" : "编辑机构账户"}</h2><button aria-label="关闭" type="button" onClick={() => setForm(null)}>×</button></div>
            {form.mode === "edit" && <div className="platform-readonly-summary"><span>机构编码：{form.institution?.code}</span><span>机构类型：{institutionTypeLabel(form.organizationType)}</span></div>}
            {form.mode === "create" && <label>机构类型<select value={form.organizationType} onChange={(event) => setForm({ ...form, organizationType: event.target.value as InstitutionType })}><option value="LAW_FIRM">律所</option><option value="FINANCIAL_INSTITUTION">金融机构</option><option value="OTHER">其他</option></select></label>}
            <label>机构名称<input autoFocus maxLength={160} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label>统一社会信用代码<input maxLength={18} value={form.creditCode} onChange={(event) => setForm({ ...form, creditCode: event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "") })} /></label>
            <label>管理员姓名<input maxLength={64} value={form.administratorName} onChange={(event) => setForm({ ...form, administratorName: event.target.value })} /></label>
            <label>管理员手机号<input inputMode="numeric" maxLength={11} value={form.administratorMobile} onChange={(event) => setForm({ ...form, administratorMobile: event.target.value.replace(/\D/g, "") })} /></label>
            {form.mode === "create" && <label>关联代理（选填）<select aria-label="创建机构关联代理" disabled={formAgentsLoading} value={form.agentPlatformUserId} onChange={(event) => setForm({ ...form, agentPlatformUserId: event.target.value })}><option value="">暂不绑定</option>{formAgents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name} · {agent.mobile}</option>)}</select></label>}
            <label>授权材料<select aria-label="授权材料要求" value={form.authorizationMaterialRequired ? "REQUIRED" : "OPTIONAL"} onChange={(event) => setForm({ ...form, authorizationMaterialRequired: event.target.value === "REQUIRED" })}><option value="REQUIRED">必传</option><option value="OPTIONAL">选填</option></select></label>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setForm(null)}>取消</button><button className="platform-primary-button" type="submit" disabled={!formReady || submitting}>{form.mode === "create" ? "创建" : "保存"}</button></div>
          </form>
        </div>
      )}

      {confirmation && (
        <div className="platform-modal-layer" role="presentation"><section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="institution-confirm-title">
          <h2 id="institution-confirm-title">{confirmation.action === "edit" ? "确认修改机构信息" : confirmation.action === "reset" ? "确认重置密码" : confirmation.action === "disable" ? "确认停用机构" : "确认启用机构"}</h2>
          <p>{confirmation.action === "edit" && <>{form?.authorizationMaterialRequired !== confirmation.institution.authorization_material_required ? <>授权材料将由“{confirmation.institution.authorization_material_required ? "必传" : "选填"}”改为“{form?.authorizationMaterialRequired ? "必传" : "选填"}”。{(form?.creditCode !== confirmation.institution.credit_code || form?.administratorMobile !== confirmation.institution.administrator_mobile) && "管理员登录手机号或信用代码也会一并更新。"}确认保存本次修改吗？</> : <>管理员登录手机号或信用代码发生变化，确认保存本次修改吗？</>}</>}{confirmation.action === "reset" && <>确认重置 <strong>{confirmation.institution.name}</strong> 管理员的登录密码吗？原密码将立即失效。</>}{confirmation.action === "disable" && <>确认停用 <strong>{confirmation.institution.name}</strong> 吗？该机构下全部账号将立即无法登录。</>}{confirmation.action === "enable" && <>确认重新启用 <strong>{confirmation.institution.name}</strong> 吗？</>}</p>
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setConfirmation(null)}>取消</button><button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void confirmAction()}>确定</button></div>
        </section></div>
      )}

      {credentials && (
        <div className="platform-modal-layer" role="presentation"><section className="platform-modal platform-credential-modal" role="dialog" aria-modal="true" aria-labelledby="institution-credential-title">
          <h2 id="institution-credential-title">{credentialsTitle}</h2><p>密码已安全保存，列表不会明文展示；以后仍可直接点击“复制账号密码”。</p>
          <dl><div><dt>登录账号</dt><dd>{credentials.account}</dd></div><div><dt>登录密码</dt><dd>••••••••••••</dd></div></dl>
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setCredentials(null)}>关闭</button><button className="platform-primary-button" type="button" onClick={() => void copyModalCredential()}>复制账号密码</button></div>
        </section></div>
      )}

      {recharge && recharge.stage === "input" && (
        <div className="platform-modal-layer" role="presentation"><form className="platform-modal" role="dialog" aria-modal="true" aria-labelledby="recharge-title" onSubmit={previewRecharge}>
          <h2 id="recharge-title">机构充值</h2><div className="platform-readonly-summary"><span>{recharge.institution.name}</span><span>当前余额：{formatCurrency(recharge.institution.balance)}</span></div>
          <label>充值金额（元）<input autoFocus inputMode="decimal" placeholder="请输入充值金额" value={recharge.amount} onChange={(event) => setRecharge({ ...recharge, amount: event.target.value.replace(/[^\d.]/g, "") })} /></label>
          <label>充值备注（选填）<textarea maxLength={512} placeholder="例如：对公款已到账" value={recharge.note} onChange={(event) => setRecharge({ ...recharge, note: event.target.value })} /></label>
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setRecharge(null)}>取消</button><button className="platform-primary-button" type="submit">下一步</button></div>
        </form></div>
      )}

      {recharge && recharge.stage === "confirm" && (
        <div className="platform-modal-layer" role="presentation"><section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="recharge-confirm-title">
          <h2 id="recharge-confirm-title">确认充值信息</h2><p>请确认本次人工充值金额，确认后将直接计入机构余额。</p>
          <dl className="platform-recharge-summary"><div><dt>机构</dt><dd>{recharge.institution.name}</dd></div><div><dt>充值前余额</dt><dd>{formatCurrency(recharge.institution.balance)}</dd></div><div><dt>本次充值</dt><dd>{formatCurrency(recharge.amount)}</dd></div><div><dt>充值后余额</dt><dd>{formatCurrency(calculateBalanceAfter(recharge.institution.balance, recharge.amount))}</dd></div></dl>
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-modal-actions"><button className="is-secondary" type="button" onClick={() => setRecharge({ ...recharge, stage: "input" })}>返回修改</button><button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void confirmRecharge()}>确认充值</button></div>
        </section></div>
      )}

      {detail && (
        <div className="platform-modal-layer" role="presentation"><section className="platform-modal platform-detail-modal" role="dialog" aria-modal="true" aria-labelledby="institution-detail-title">
          <div className="platform-modal-heading"><h2 id="institution-detail-title">机构详情</h2><button aria-label="关闭" type="button" onClick={() => setDetail(null)}>×</button></div>
          <div className="platform-detail-basics"><div><span>机构名称</span><strong>{detail.name}</strong></div><div><span>机构编码</span><strong>{detail.code}</strong></div><div><span>机构类型</span><strong>{institutionTypeLabel(detail.organization_type)}</strong></div><div><span>当前余额</span><strong>{formatCurrency(detail.balance)}</strong></div><div><span>统一社会信用代码</span><strong>{detail.credit_code}</strong></div><div><span>管理员</span><strong>{detail.administrator_name} · {detail.administrator_mobile}</strong></div><div><span>授权材料</span><strong>{detail.authorization_material_required ? "必传" : "选填"}</strong></div></div>
          <h3>充值记录</h3><div className="platform-table-wrap"><table className="platform-history-table"><thead><tr><th>流水号</th><th>充值金额</th><th>充值前</th><th>充值后</th><th>操作人</th><th>备注</th><th>时间</th></tr></thead><tbody>
            {!historyLoading && history.map((item) => <tr key={item.id}><td>{item.reference}</td><td>{formatCurrency(item.amount)}</td><td>{formatCurrency(item.balance_before)}</td><td>{formatCurrency(item.balance_after)}</td><td>{item.operator_name}</td><td>{item.note || "—"}</td><td>{formatChinaDateTime(item.created_at)}</td></tr>)}
            {historyLoading && <tr><td colSpan={7}>正在加载充值记录…</td></tr>}{!historyLoading && history.length === 0 && <tr><td colSpan={7}>暂无充值记录</td></tr>}
          </tbody></table></div>
          {modalError && <p className="platform-modal-error">{modalError}</p>}
          <div className="platform-pagination"><span>共 {historyTotal} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={historyPage <= 1 || historyLoading} onClick={() => setHistoryPage((value) => value - 1)}>上一页</button><span>{historyPage} / {pageCount(historyTotal)}</span><button type="button" disabled={historyPage >= pageCount(historyTotal) || historyLoading} onClick={() => setHistoryPage((value) => value + 1)}>下一页</button></div></div>
        </section></div>
      )}

      {productInstitution && <InstitutionProducts institution={productInstitution} onClose={() => setProductInstitution(null)} />}

      {agentInstitution && <InstitutionAgentDialog institution={agentInstitution} onClose={() => setAgentInstitution(null)} onSaved={savedAgent} />}

      {toast && <div className="platform-toast" role="status">{toast}</div>}
    </section>
  );
}
